import styled from '@emotion/styled';
import { AddOns } from '@racemap/sdk/schema/billing/AddOns';
import { EventTypes, type ModulesStates } from '@racemap/sdk/schema/events';
import { MaxMobileWindowWidth } from '@racemap/utilities/consts/common';
import { isActivated, isEnabled } from '@racemap/utilities/functions/event';
import { isAtomicEvent } from '@racemap/utilities/functions/utils';
import type { OptionModules } from '@racemap/utilities/types/events';
import { Space, Tag } from 'antd';
import type { Immutable } from 'immer';
import type { FC, ReactNode } from 'react';
import { Helmet } from 'react-helmet';
import { Outlet } from 'react-router-dom';
import { getEventTypeDocuLink } from 'src/lib/EventUtils';
import { useCurrentEvent } from 'src/lib/customHooks/useCurrentEvent';
import { useIsAdmin } from 'src/lib/customHooks/useIsAdmin';
import { CompactNumber } from 'src/misc/formatNumber';
import { type CurrentEvent, useEventEditorActions } from 'src/store/events/events_reducers';
import { useStore } from '../../store/reducers';
import {
  IconAdmin,
  IconAdvertising,
  IconBell,
  IconChartLine,
  IconDatafeed,
  IconDocs,
  IconExternal,
  IconKey,
  IconLeaderboard,
  IconLink,
  IconMap,
  IconPaintRoller,
  IconPrediction,
  IconRunner,
  IconStart,
  IconWrench,
} from '../BasicComponents/Icons';
import type { Sections, Tabs } from '../BasicComponents/Sidebar';
import { EventDuplicateModal } from '../EventDuplicateModal';
import { EventEditorSidebar } from './EventEditorSidebar';
import { SyncParticipantsReportModal } from './ParticipantTab/SyncParticipantsReportModal';
import { PaymentModal } from './PaymentModal';
import { TitleBar } from './TitleBar';

enum SectionKeys {
  ACTIVATED = 0,
  AVAILABLE = 1,
  ADVANCED = 2,
  ADMIN = 3,
  ESSENTIALS = 4,
}

export const EventEditor: FC = () => {
  const isAdmin = useIsAdmin();
  const event = useCurrentEvent();
  const showPaymentModal = useStore((s) => s.events.showPaymentModal);
  const showStarterSyncModal = useStore((s) => s.events.showStarterSyncModal);
  const { closePaymentModal, closeStarterSyncModal } = useEventEditorActions();

  if (event == null) return null;

  const sections: Sections = [
    {
      key: SectionKeys.ESSENTIALS,
      label: 'Essentials',
      status: 'primary',
    },
    {
      key: SectionKeys.ACTIVATED,
      label: 'Activated',
      status: 'success',
    },
    {
      key: SectionKeys.AVAILABLE,
      label: 'Available',
      status: 'primary',
    },
    {
      key: SectionKeys.ADVANCED,
      label: 'Expert',
      status: 'primary',
    },
    {
      key: SectionKeys.ADMIN,
      label: 'Admin',
      status: 'primary',
    },
  ];

  const tabs: Tabs = [
    ...getBasicTabsBasedOnEventType(event),
    {
      entryKey: 'analytics',
      title: 'Analytics',
      path: 'analytics',
      icon: <IconChartLine />,
      sectionKey: SectionKeys.ADVANCED,
    },
    {
      entryKey: 'integrations',
      title: 'Integrations',
      path: 'integrations',
      icon: <IconLink />,
      sectionKey: SectionKeys.ADVANCED,
    },
    ...(isAdmin
      ? [
          {
            entryKey: 'admin',
            title: 'Admin',
            path: 'admin',
            icon: <IconAdmin />,
            sectionKey: SectionKeys.ADMIN,
          },
        ]
      : []),
  ];

  if (event == null) return <></>;
  return (
    <Container className="event-editor">
      <Helmet>
        <title>{event?.name ? `Racemap | ${event.name}` : 'Racemap | Admin'}</title>
      </Helmet>
      <TitleBar />
      <Body>
        <EventEditorSidebar tabs={tabs} sections={sections} />

        <Content className="event-editor-content">
          <Outlet />
        </Content>
      </Body>

      {/* Modals */}
      <EventDuplicateModal event={event} />
      <PaymentModal show={showPaymentModal} onClose={closePaymentModal} />
      <SyncParticipantsReportModal open={showStarterSyncModal} onCancel={closeStarterSyncModal} />
    </Container>
  );
};

const getBasicTabsBasedOnEventType = (event: Immutable<CurrentEvent>): Tabs => {
  const atomicEvent = isAtomicEvent(event) && event.type !== EventTypes.SAFEMAP;
  const starterCount = event.startersCount || undefined;

  if (atomicEvent) {
    return [
      {
        entryKey: 'basic',
        title: 'Basic',
        path: 'basic',
        icon: <IconStart />,
        sectionKey: SectionKeys.ESSENTIALS,
      },
      {
        entryKey: 'map',
        title: 'Map',
        path: 'map',
        icon: <IconMap />,
        sectionKey: SectionKeys.ESSENTIALS,
      },
      {
        entryKey: 'visualisation',
        title: 'Visualisation',
        path: 'visualisation',
        icon: <IconPaintRoller />,
        sectionKey: SectionKeys.ESSENTIALS,
      },
      {
        entryKey: 'prediction',
        title: 'Prediction',
        path: 'prediction',
        icon: <IconPrediction />,
        sectionKey: getAddonSection(AddOns.PREDICTIVE, event.modules),
        badge: getActivationBadge(event.modules.predictive),
      },
      {
        entryKey: 'advanced',
        title: 'Advanced',
        path: 'advanced',
        icon: <IconWrench />,
        sectionKey: SectionKeys.ADVANCED,
      },
      {
        entryKey: 'dataapi',
        title: 'Data API',
        path: 'dataapi',
        icon: <IconDatafeed />,
        sectionKey: getAddonSection(AddOns.API, event.modules),
        badge: getActivationBadge(event.modules.dataFeed),
      },
      {
        entryKey: 'passcode_keys',
        title: 'Passcode/Keys',
        path: 'passcode_keys',
        icon: <IconKey />,
        sectionKey: SectionKeys.ADVANCED,
      },
      {
        entryKey: 'sponsor',
        title: 'Sponsor Logo',
        path: 'sponsor',
        icon: <IconAdvertising />,
        sectionKey: getAddonSection(AddOns.SPONSOR, event.modules),
        badge: getActivationBadge(event.modules.sponsor),
      },
      {
        entryKey: 'participants',
        title: 'Participants',
        path: 'participants',
        icon: <IconRunner />,
        sectionKey: SectionKeys.ESSENTIALS,
        badge: getCountBadge(starterCount),
      },
      {
        entryKey: 'leaderboard',
        title: 'Leaderboard',
        path: 'leaderboard',
        icon: <IconLeaderboard />,
        sectionKey: getAddonSection(AddOns.TIMING, event.modules),
        badge: getActivationBadge(event.modules.timing),
      },
      {
        entryKey: 'documentation',
        title: (
          <Space>
            Documentation
            <IconExternal />
          </Space>
        ),
        path: `https://docs.racemap.com/${getEventTypeDocuLink(event.type)}`,
        icon: <IconDocs />,
        sectionKey: SectionKeys.ESSENTIALS,
      },
    ];
  }

  if (event.type === EventTypes.STAGE_GROUP) {
    return [
      {
        entryKey: 'groupbasic',
        title: 'Groupbasic',
        path: 'basic',
        icon: <IconStart />,
        sectionKey: SectionKeys.ESSENTIALS,
      },
      {
        entryKey: 'participants',
        title: 'Participants',
        path: 'participants',
        icon: <IconRunner />,
        sectionKey: SectionKeys.ESSENTIALS,
        badge: getCountBadge(starterCount),
      },
      {
        entryKey: 'passcode_keys',
        title: 'Passcode/Keys',
        path: 'passcode_keys',
        icon: <IconKey />,
        sectionKey: SectionKeys.ADVANCED,
      },
      {
        entryKey: 'leaderboard',
        title: 'Leaderboard',
        path: 'leaderboard',
        icon: <IconLeaderboard />,
        sectionKey: getAddonSection(AddOns.TIMING, event.modules),
        badge: getActivationBadge(event.modules.timing),
      },
      {
        entryKey: 'documentation',
        title: (
          <Space>
            Documentation
            <IconExternal />
          </Space>
        ),
        path: `https://docs.racemap.com/${getEventTypeDocuLink(event.type)}`,
        icon: <IconDocs />,
        sectionKey: SectionKeys.ESSENTIALS,
      },
      {
        entryKey: 'advanced',
        title: 'Advanced',
        path: 'advanced',
        icon: <IconWrench />,
        sectionKey: SectionKeys.ADVANCED,
      },
    ];
  }

  if (event.type === EventTypes.CONTEST_GROUP) {
    return [
      {
        entryKey: 'groupbasic',
        title: 'Basic',
        path: 'basic',
        icon: <IconStart />,
        sectionKey: SectionKeys.ESSENTIALS,
      },
      {
        entryKey: 'documentation',
        title: (
          <Space>
            Documentation
            <IconExternal />
          </Space>
        ),
        path: `https://docs.racemap.com/${getEventTypeDocuLink(event.type)}`,
        icon: <IconDocs />,
        sectionKey: SectionKeys.ESSENTIALS,
      },
      {
        entryKey: 'advanced',
        title: 'Advanced',
        path: 'advanced',
        icon: <IconWrench />,
        sectionKey: SectionKeys.ADVANCED,
      },
    ];
  }

  if (event.type === EventTypes.SAFEMAP) {
    return [
      {
        entryKey: 'basic',
        title: 'Basic',
        path: 'basic',
        icon: <IconStart />,
        sectionKey: SectionKeys.ESSENTIALS,
      },
      {
        entryKey: 'advanced',
        title: 'Advanced',
        path: 'advanced',
        icon: <IconWrench />,
        sectionKey: SectionKeys.ADVANCED,
      },
      {
        entryKey: 'map',
        title: 'Map',
        path: 'map',
        icon: <IconMap />,
        sectionKey: SectionKeys.ESSENTIALS,
      },
      {
        entryKey: 'participants',
        title: 'Participants',
        path: 'participants',
        icon: <IconRunner />,
        sectionKey: SectionKeys.ESSENTIALS,
        badge: getCountBadge(starterCount),
      },
      {
        entryKey: 'alerts',
        title: 'Alerts',
        path: 'alerts',
        icon: <IconBell />,
        sectionKey: SectionKeys.ESSENTIALS,
      },
      {
        entryKey: 'documentation',
        title: (
          <Space>
            Documentation
            <IconExternal />
          </Space>
        ),
        path: `https://docs.racemap.com/${getEventTypeDocuLink(event.type)}`,
        icon: <IconDocs />,
        sectionKey: SectionKeys.ESSENTIALS,
      },
    ];
  }

  throw new Error('Unknown event type');
};

const Container = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  min-height: 100vh;
  display: flex;
  padding-top: 104px;
  bottom: 0;

  /* the page pill adds 38px below the title bar */
  @media (max-width: ${MaxMobileWindowWidth}px) {
    padding-top: 142px;
    flex-direction: column;
    min-height: unset;
  }
`;

const Body = styled.div`
  display: flex;
  flex: 1;

  @media (max-width: ${MaxMobileWindowWidth}px) {
    flex-direction: column;
  }
`;

const Content = styled.div`
  position: absolute;
  right: 0;
  left: 200px;
  bottom: 0;
  top: 104px;
  overflow: auto;

  @media (max-width: ${MaxMobileWindowWidth}px) {
    position: relative;
    left: unset;
    right: unset;

    top: unset;
    padding-left: 0;
    margin-left: 0;
    flex: 1;
  }
`;

function getAddonSection(addon: AddOns, modules: Immutable<OptionModules>): SectionKeys {
  switch (addon) {
    case AddOns.API:
      return getTabSection(modules.dataFeed);
    case AddOns.SPONSOR:
      return getTabSection(modules.sponsor);
    case AddOns.PREDICTIVE:
      return isActivated(modules.predictive) ? SectionKeys.ACTIVATED : SectionKeys.AVAILABLE;
    case AddOns.TIMING:
      return getTabSection(modules.timing);
    default:
      return SectionKeys.AVAILABLE;
  }
}

function getTabSection(
  module: Immutable<{ state?: ModulesStates; enabled?: boolean }>,
): SectionKeys {
  return isActivated(module) || isEnabled(module) ? SectionKeys.ACTIVATED : SectionKeys.AVAILABLE;
}

function getActivationBadge(
  module: Immutable<{ state?: ModulesStates; enabled?: boolean }>,
): ReactNode {
  if (getTabSection(module) !== SectionKeys.ACTIVATED) return <></>;
  if (isActivated(module))
    return (
      <Tag color="green" variant="filled">
        ON
      </Tag>
    );

  return (
    <Tag color="orange" variant="filled">
      OFF
    </Tag>
  );
}

function getCountBadge(count?: number): ReactNode {
  if (count == null) return <></>;
  return (
    <Tag variant="filled">
      <CompactNumber value={count} showZero />
    </Tag>
  );
}
